import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { subDays } from 'date-fns'
import { ThemeProvider } from 'styled-components'
import { useTheme } from '@mui/material/styles'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { toDateString } from '@/v2/utils/dateHelpers'
import { usePaymentsDashboard } from '@/v2/hooks/usePaymentsDashboard'
import { DashboardContainer, LoadingContainer } from './styles'
import {
  DashboardHeader,
  BillingFilters,
  SummaryCards,
  PaymentsTable,
} from './components'

const DEFAULT_FILTERS = () => ({
  startDate: toDateString(subDays(new Date(), 30)),
  endDate: toDateString(new Date()),
  method: '',
  paymentStatus: '',
  search: '',
})

const DEFAULT_PER_PAGE = 25

const BillingDashboard = () => {
  const theme = useTheme()
  const initialFilters = useMemo(DEFAULT_FILTERS, [])
  const [filters, setFilters] = useState(initialFilters)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)

  const {
    summary,
    payments,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
  } = usePaymentsDashboard(filters, { page, perPage })

  // Any filter change resets us to the first page so the user never lands on
  // an out-of-range page for the newly filtered result set.
  useEffect(() => {
    setPage(1)
  }, [filters])

  // Takes a patch and merges it into the previous state rather than a fully
  // built object: the date presets emit startDate and endDate as two separate
  // calls, and a value captured at render would make the second overwrite the
  // first.
  const handleFiltersChange = useCallback(
    patch => setFilters(prev => ({ ...prev, ...patch })),
    []
  )

  const handleClear = useCallback(() => setFilters(DEFAULT_FILTERS()), [])

  const handlePageChange = useCallback(nextPage => setPage(nextPage), [])

  const handlePerPageChange = useCallback(nextPerPage => {
    setPerPage(nextPerPage)
    setPage(1)
  }, [])

  return (
    <ThemeProvider theme={theme.palette}>
      <DashboardContainer>
        <DashboardHeader onRefresh={refetch} />
        <BillingFilters
          filters={filters}
          onChange={handleFiltersChange}
          onClear={handleClear}
        />
        {isLoading ? (
          <LoadingContainer>
            <CircularProgress size={48} thickness={4} />
            <Typography variant="h6" color="textSecondary">
              Loading payments...
            </Typography>
          </LoadingContainer>
        ) : isError ? (
          <LoadingContainer>
            <Typography variant="h6" color="error">
              {error?.message || 'Something went wrong loading payments.'}
            </Typography>
          </LoadingContainer>
        ) : (
          <>
            <SummaryCards summary={summary} />
            <PaymentsTable
              payments={payments}
              pagination={pagination}
              filters={filters}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </>
        )}
      </DashboardContainer>
    </ThemeProvider>
  )
}

export default BillingDashboard
